<script setup>
import { FormInput, FormLabel, FormSwitch, FormTextarea } from "@Components/Form";
import { PrimaryButton } from "@Components/Button";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import Badge from "@Components/Badge.vue";
import Tooltip from "@Components/Tooltip.vue";
import { MagicIcon } from "@Icons/index";
import { useForm } from "@/form";

const modelValue = defineModel();

const { t: __ } = useI18n();

const copied = ref(undefined);
const generatePromptForm = useForm({
    description: "",
});

const variables = [
    {
        name: "TITLE",
        description: "Remplacée par le titre de la publication.",
    },
];

const generatePrompt = async () => {
    if (generatePromptForm.processing) return;

    if (generatePromptForm.description.length === 0) return;

    const response = await generatePromptForm.post("text_blocks_generate_prompt");

    if (response.prompt && response.prompt.length > 0) {
        modelValue.value.prompt = response.prompt;
    }
};

const copyVariable = async (variable) => {
    try {
        await navigator.clipboard.writeText("[" + variable.name + "]");
        copied.value = variable.name;
        setTimeout(() => {
            copied.value = undefined;
        }, 2000);
    } catch (err) {}
};
</script>

<template>
    <div class="space-y-6">
        <FormInput :z="1" v-model="modelValue.name" :label="__('Nom du block')" />

        <FormSwitch :z="1" v-model="modelValue.use_live_data">{{ __("Utiliser la web data") }}</FormSwitch>

        <div class="space-y-3">
            <FormSwitch :z="1" v-model="modelValue.external_links_enabled">{{ __("Autoriser les liens externes") }}</FormSwitch>

            <div v-if="modelValue.external_links_enabled" class="ml-7 space-y-3">
                <FormSwitch :z="1" v-model="modelValue.external_links_no_follow">{{ __("No-follow") }}</FormSwitch>
                <FormSwitch :z="1" v-model="modelValue.external_links_blank">{{ __("Blank") }}</FormSwitch>
            </div>
        </div>

        <FormSwitch :z="1" v-model="modelValue.internal_links_enabled">{{ __("Autoriser les liens internes") }}</FormSwitch>

        <div class="space-y-6">
            <div>
                <FormLabel for="generate-ai-prompt">{{ __("Générer un prompt") }}</FormLabel>
                <div class="flex items-center mt-2 space-x-3">
                    <div class="flex-1">
                        <FormInput
                            id="generate-ai-prompt"
                            :z="1"
                            v-model="generatePromptForm.description"
                            :placeholder="__('Une introduction courte et précise')" />
                    </div>
                    <div class="flex-none">
                        <PrimaryButton :z="1" type="button" :processing="generatePromptForm.processing" @click="generatePrompt" :icon="MagicIcon">
                            {{ __("Générer") }}
                        </PrimaryButton>
                    </div>
                </div>
            </div>

            <FormTextarea :z="1" v-model="modelValue.prompt" rows="8" id="ai-prompt" :label="__('Prompt')">
                <template #hint>
                    <div class="flex items-center space-x-3">
                        <div class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            {{ __("Variable :") }}
                        </div>

                        <div class="-m-1.5 flex flex-wrap">
                            <div v-for="variable in variables" class="p-1.5">
                                <Tooltip :content="__(variable.description)">
                                    <Badge @click="copyVariable(variable)" class="relative cursor-pointer">
                                        <span :class="[copied === variable.name ? 'invisible' : '']">[{{ variable.name }}] </span>
                                        <span v-if="copied === variable.name" class="absolute inset-0 inline-flex justify-center items-center">
                                            <span>{{ __("Copié !") }}</span>
                                        </span>
                                    </Badge>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                </template>
            </FormTextarea>
        </div>
    </div>
</template>
